import { type RefObject, useRef, useState } from 'react'
import type React from 'react'
import {
  type TextOnPathDirection,
  projectClientPointToStartDistance,
} from '../helpers/text-on-path-projection'

export interface TextOnPathHandleDrag {
  isDragging: boolean
  onPointerDown: (event: React.PointerEvent<SVGCircleElement>) => void
  onPointerMove: (event: React.PointerEvent<SVGCircleElement>) => void
  /** Also used for pointer-cancel. */
  onPointerUp: (event: React.PointerEvent<SVGCircleElement>) => void
}

interface TextOnPathHandleDragOptions {
  shapeId: string
  pathRef: RefObject<SVGPathElement | null>
  pathLength: number
  direction: TextOnPathDirection
  /** Without a listener the dot is display-only and these handlers do nothing. */
  onStartDistanceChange?: (shapeId: string, startDistance: number) => void
}

/**
 * Dragging the text-on-path start dot. The drag lives with the renderer rather than the action bar
 * because the projection needs the path element itself.
 */
export function useTextOnPathHandleDrag({
  shapeId,
  pathRef,
  pathLength,
  direction,
  onStartDistanceChange,
}: TextOnPathHandleDragOptions): TextOnPathHandleDrag {
  const draggingRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)

  return {
    isDragging,
    onPointerDown: (event) => {
      if (!onStartDistanceChange || event.button !== 0) {
        return
      }

      event.preventDefault()
      // The canvas would otherwise read this as a click on the shape and change the selection out
      // from under the open action bar.
      event.stopPropagation()
      draggingRef.current = true
      setIsDragging(true)
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    onPointerMove: (event) => {
      if (!draggingRef.current || !onStartDistanceChange) {
        return
      }

      const next = projectClientPointToStartDistance(
        pathRef.current,
        event.clientX,
        event.clientY,
        pathLength,
        direction
      )

      if (next != null) {
        onStartDistanceChange(shapeId, next)
      }
    },
    onPointerUp: (event) => {
      if (!draggingRef.current) {
        return
      }

      draggingRef.current = false
      setIsDragging(false)

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    },
  }
}
